import { useEffect, useRef } from 'react'
import { useToast } from '@/hooks/use-toast'

export interface CollectedError {
    timestamp: string
    status: number
    message: string
    url: string
}

// In-memory error ring buffer — capped at 100 entries
const errorBuffer: CollectedError[] = []
const MAX_ERRORS = 100

function pushError(err: CollectedError) {
    errorBuffer.push(err)
    if (errorBuffer.length > MAX_ERRORS) errorBuffer.shift()
}

/** Get all collected errors (most recent first) */
export function getCollectedErrors(): CollectedError[] {
    return [...errorBuffer].reverse()
}

/** Clear the error buffer */
export function clearCollectedErrors() {
    errorBuffer.length = 0
}

/**
 * Listens for global API error events, stores them, and shows toast notifications.
 * Deduplicates errors within a short window to prevent toast floods.
 */
export function ErrorNotificationProvider({ children }: { children: React.ReactNode }) {
    const { toast } = useToast()
    const recentErrors = useRef(new Map<string, number>())

    useEffect(() => {
        const handler = (e: Event) => {
            const { status, message, url } = (e as CustomEvent).detail || {}
            const now = Date.now()

            // Always store the error
            pushError({
                timestamp: new Date().toISOString(),
                status: status || 0,
                message: message || 'Unknown error',
                url: url || '',
            })

            // Deduplicate toast: skip if same error within 3 seconds
            const key = `${status}:${url || ''}`
            const lastSeen = recentErrors.current.get(key)
            if (lastSeen && now - lastSeen < 3000) return
            recentErrors.current.set(key, now)

            if (recentErrors.current.size > 50) {
                for (const [k, t] of recentErrors.current) {
                    if (now - t > 10000) recentErrors.current.delete(k)
                }
            }

            const isServerError = status >= 500
            const isNetworkError = status === 0

            toast({
                variant: 'destructive',
                title: isNetworkError
                    ? 'Connection Error'
                    : isServerError
                    ? 'Oops! Something went wrong'
                    : 'Error',
                description: isServerError
                    ? "This one's on us — please try again in a moment."
                    : message || 'An unexpected error occurred.',
            })
        }

        window.addEventListener('api-error', handler)
        return () => window.removeEventListener('api-error', handler)
    }, [toast])

    return <>{children}</>
}
